import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Icon, type IconName } from '../Icon/Icon';
import { COLORS } from '../../theme/tokens';
import { Root, BackButton, Title, TitleButton, TitleStack, ActionSlot } from './BackHeader.styles';

export type BackHeaderProps = {
  /**
   * Centred band title. Omit it to collapse the band to the back arrow's height (for a page
   * that shows its own heading below); pass `backLabel` in that case so the back button stays
   * named for assistive tech. Every drill-in page that wants the gradient title still passes it.
   */
  title?: string;
  /**
   * Accessible name for the back button. Defaults to a generic "Back"; pass a context-aware
   * label (e.g. "Back from Profile") when `title` is omitted so screen readers still announce
   * which section is being left.
   */
  backLabel?: string;
  /** Defaults to browser-history back (`navigate(-1)`). */
  onBack?: () => void;
  /**
   * Primary page action pinned to the right edge of the band (mirrors the back
   * arrow on the left), e.g. an Edit `Button` on a preview page. The centred
   * title narrows to stay clear of it.
   */
  action?: React.ReactNode;
  /**
   * On phones (≤820px) drop the action onto its own right-aligned row under the
   * title instead of pinning it beside it. For an action cluster too wide to
   * share the row with a centred title (the article editor's Generate + preview
   * + settings trio); a single button fits pinned and should stay put.
   */
  stackActionOnMobile?: boolean;
  /** The title is data (a name as typed), shown as given rather than looked up as copy. */
  titleRaw?: boolean;
  /** The title is a control: a press on it does this (e.g. renaming what it names). */
  onTitleClick?: () => void;
  /** With `onTitleClick`: the glyph after the title saying what a press does, and the control's accessible name. */
  titleIcon?: IconName;
  titleLabel?: string;
  /** The action is a single icon button: the centred title keeps clear of a pinned edge no wider than the back arrow's
   *  (with a wide action it keeps clear of the action pill). */
  narrowAction?: boolean;
  /** A line under the title, centred with it (a tag saying what the title names); the back arrow and the action stay
   *  level with the title. */
  subtitle?: React.ReactNode;
  /** The action has opened across the row (a publish asking for its second press): its slot spans the row and the
   *  title and the back arrow under it fade out. */
  actionOpen?: boolean;
};

/** Back arrow + centred title for the drill-in detail pages (campaign, channel, …). */
export const BackHeader = ({
  title,
  backLabel,
  onBack,
  action,
  stackActionOnMobile,
  titleRaw = false,
  onTitleClick,
  titleIcon,
  titleLabel,
  narrowAction = false,
  subtitle,
  actionOpen = false,
}: BackHeaderProps): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleBack = onBack ?? (() => navigate(-1));
  return (
    <Root
      $stack={Boolean(action) && stackActionOnMobile}
      $titleless={!title}
      $subtitled={Boolean(subtitle && title)}
      $actionOpen={actionOpen && Boolean(action)}
    >
      <BackButton type="button" aria-label={backLabel ?? t('Back')} onClick={handleBack}>
        <Icon name="arrow-left" size={22} color={COLORS.text} />
      </BackButton>
      {(() => {
        const wide = Boolean(action) && !narrowAction;
        const titled =
          title && onTitleClick ? (
            <TitleButton type="button" aria-label={titleLabel} $withAction={wide && !subtitle} onClick={onTitleClick}>
              <Title text={title} raw={titleRaw} tag="span" />
              {titleIcon ? <Icon name={titleIcon} size={16} color={COLORS.textMuted} /> : null}
            </TitleButton>
          ) : title ? (
            <Title text={title} raw={titleRaw} tag="span" $withAction={wide && !subtitle} />
          ) : null;
        return subtitle && titled ? (
          <TitleStack $withAction={wide}>
            {titled}
            {subtitle}
          </TitleStack>
        ) : (
          titled
        );
      })()}
      {action ? <ActionSlot>{action}</ActionSlot> : null}
    </Root>
  );
};

export default BackHeader;
